import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import * as THREE from 'three';
import { toRequestState } from '../../../../../data-request/operators';
import { InstancedDynamicBox } from '../../../../../models_new/classes/3dview/dynamic-box';
import { LabelOrientation } from '../../../../../models_new/enums/label-orientation';
import { NewAssetStoreService } from '../../../../../services/3dview/asset-store.service';
import { NotificationService } from '../../../../../services/notification.service';
import { OrganizationLogoService } from '../../../../../services/organization-logo.service';
import { New_ThreeViewComponent } from '../../three-view/three-view.component';
import { RenderingService } from '../../../../../services/3dview/rendering.service';
import { AssetIDs } from 'src/app/models_new/enums/asset-ids';

@Component({
  selector: 'app-production-line-view',
  // Doesn't need a custom template.
  templateUrl: '../../three-view/three-view.component.html',
  styleUrls: [
    '../../three-view/three-view.component.scss',
    './production-line-view.component.scss',
  ],
  // Production line view alone doesn't update the crash overlay...
  // To fix this it's set to onPush detection strategy and updated manually.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionLineViewComponent
  extends New_ThreeViewComponent
  implements OnInit, OnChanges, OnDestroy
{
  X_OFFSET = -5; // Offset along the x-axis to make sure the boxes starts outside of view
  PADDING = 3; // meters
  DIMENSION = 1; // meters
  COUNT = 4; // Number of boxes to display

  boxes: InstancedDynamicBox;

  @Input() cpm: number = 0;

  speed = 0.0; // m/s
  position = 0.0; // The property we are animating

  // Helper to make it easy to create transform matrices
  dummy = new THREE.Object3D();

  constructor(
    ngZone: NgZone,
    private compLogoService: OrganizationLogoService,
    private assetStore: NewAssetStoreService,
    public notification: NotificationService,
    renderingService: RenderingService,
    cdRef: ChangeDetectorRef
  ) {
    super(ngZone, renderingService, cdRef);

    this.loading$ = this.assetStore.load(AssetIDs.Box).data$.pipe(
      map((box: THREE.Object3D) => {
        // We know the structure of the box model, the first child is the actual mesh
        this.boxes = new InstancedDynamicBox(
          box.children[0] as THREE.Mesh,
          this.COUNT
        );

        // Space boxes out along the x axis
        for (let i = 0; i < this.COUNT; i++) {
          this.dummy.position.x = (this.DIMENSION + this.PADDING) * i;

          // This is the default box, just 5x.
          this.dummy.scale.x = 1.45;
          this.dummy.scale.y = 1.0;
          this.dummy.scale.z = 0.95;

          this.dummy.updateMatrix();
          this.boxes.setMatrixAt(i, this.dummy.matrix);
        }

        this.scene.add(this.boxes);

        return false;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error) => {
        console.error(error);
        const msg =
          "Couldn't load asset. Please try to refresh. Contact MRC support if the error persists.";
        this.notification.showError(msg);
        return throwError(() => new Error(msg));
      }),
      startWith(true),
      toRequestState()
    );

    // Needs to be handled after we have a box to update
    this.loading$
      .pipe(
        takeUntil(this.destroy$),
        filter((v) => v.value),
        take(1),
        switchMap(() => {
          return this.compLogoService.logoTexture$;
        })
      )
      .subscribe((texture: THREE.Texture) => {
        if (this.boxes) {
          this.boxes.setLabels(texture, [LabelOrientation.FRONT]);
        }
      });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.camera.position.set(7, 0.8, 2.5);
    this.controls.target.set(6, 0.0, 0.0);
    this.controls.enableRotate = false;
    this.controls.enableZoom = false;
    this.controls.enablePan = false;
    this.controls.update();

    this.addLighting();

    this.enableRenderingLoop(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (
      changes.cpm?.currentValue !== undefined ||
      changes.cpm?.currentValue !== null
    ) {
      // We need to convert cases per minute to a speed
      // To convert to seconds we divide by 60. And since we need to process both
      // the box and the padding for each box, we multiply by DIMENSION + PADDING
      this.speed = (this.cpm * (this.DIMENSION + this.PADDING)) / 60;
    }

    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  update(dt?: number | null): void {
    this.updatePositions(dt);
  }

  private updatePositions(dt: number) {
    if (!this.boxes) {
      return;
    }

    this.position += this.speed * dt;

    if (this.position > this.DIMENSION + this.PADDING) {
      // Make we never move the position more than the distane between boxes.
      // This will appears as if the box at the end is moved to the front of the queue.
      this.position = this.position % (this.DIMENSION + this.PADDING);
    }

    // Move the boxes
    this.boxes.position.x = this.position;
  }

  private addLighting(): void {
    const ambientIntensity = 0.2;
    const directionalIntensity = 0.8;

    const ambient = new THREE.AmbientLight('#ffffff', ambientIntensity);
    this.scene.add(ambient);

    const directional = new THREE.DirectionalLight('#ffffff');
    directional.position.set(-5, 3, 5);
    directional.intensity = directionalIntensity;
    directional.target.position.copy(new THREE.Vector3());
    this.scene.add(directional.target);
    this.scene.add(directional);
  }
}
